/**
 * Drawing snaps and typed input (pure, tested in snaps.test.ts): where the next corner or line end lands while the
 * installer draws, and why (the canvas draws the guides and the cue the same function returns, so it never lies).
 *
 * In order: an existing corner; the middle of a line; the crossing of two guides; a point on a line; an extension
 * of a line beyond its end; a direction from the last point perpendicular or parallel to a drawn line; a 15° step
 * from the house's main direction. Alt turns every snap off. A typed length (and angle) puts the point exactly:
 * "6.5" is 6.5 m in the current direction, "6.5<90" 6.5 m at 90° to the previous side (or from east when there is
 * none).
 */
import type { Vec2 } from "../editor/geo.ts";
import { closestOnSegment, mainDirection, type Sketch } from "./skeleton.ts";
import type { Text } from "./text.ts";

export type SnapKind = "corner" | "midpoint" | "intersection" | "on-line" | "extension" | "perpendicular" | "parallel" | "angle" | "typed" | "free";

export interface Guide {
  kind: "extension" | "perpendicular" | "parallel" | "angle";
  /** A point on the guide and its direction (unit). */
  from: Vec2;
  dir: Vec2;
}

export interface SmartSnap {
  point: Vec2;
  kind: SnapKind;
  /** The vertex or line the point is on (what `addLine` joins or splits). */
  vertex?: string;
  edge?: string;
  guides: Guide[];
  /** The cue's own text where the kind does not say it (an angle: "45°"); the kinds' words are in the catalog
   * (`sketch.snap.<kind>`, SketchCanvas `SmartCue`). */
  label: string;
  /** Length from the last point and its angle (degrees, counter-clockwise from east) and turn from the previous side. */
  length: number | null;
  angle: number | null;
  turn: number | null;
}

export const ANGLE_STEP = 15;
const DIRECTION_TOL_DEG = 3;
const RAD = Math.PI / 180;

const sub = (a: Vec2, b: Vec2): Vec2 => [a[0] - b[0], a[1] - b[1]];
const len = (a: Vec2) => Math.hypot(a[0], a[1]);
const unit = (a: Vec2): Vec2 => {
  const l = len(a) || 1;
  return [a[0] / l, a[1] / l];
};
const dist = (a: Vec2, b: Vec2) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const norm180 = (d: number) => ((((d + 180) % 360) + 360) % 360) - 180;

/** Projection of `q` onto the line through `from` along `dir` (unit). */
export function project(from: Vec2, dir: Vec2, q: Vec2): Vec2 {
  const t = (q[0] - from[0]) * dir[0] + (q[1] - from[1]) * dir[1];
  return [from[0] + dir[0] * t, from[1] + dir[1] * t];
}

/** Where two guides cross (null when parallel). */
export function crossGuides(g: Guide, h: Guide): Vec2 | null {
  const den = g.dir[0] * h.dir[1] - g.dir[1] * h.dir[0];
  if (Math.abs(den) < 1e-6) return null;
  const t = ((h.from[0] - g.from[0]) * h.dir[1] - (h.from[1] - g.from[1]) * h.dir[0]) / den;
  return [g.from[0] + g.dir[0] * t, g.from[1] + g.dir[1] * t];
}

function measure(anchor: Vec2 | null, prevDeg: number | null, p: Vec2) {
  if (!anchor || dist(anchor, p) < 1e-9) return { length: anchor ? 0 : null, angle: null, turn: null };
  const angle = Math.atan2(p[1] - anchor[1], p[0] - anchor[0]) / RAD;
  return { length: dist(anchor, p), angle, turn: prevDeg == null ? null : norm180(angle - prevDeg) };
}

interface SnapInput {
  sketch: Sketch;
  pointer: Vec2;
  /** The last point placed (a line's start, the previous footprint corner); null before the first. */
  anchor: Vec2 | null;
  /** Direction of the previous side (degrees), for the turn and the 90° default. */
  prevDeg?: number | null;
  /** Snap distance in metres (about ten screen pixels). */
  tol: number;
  /** Extra points to snap to (footprint corners drawn so far). */
  points?: Vec2[];
  /** Extra lines (the footprint's sides drawn so far): guides like the drawn lines, not split by a click. */
  segments?: [Vec2, Vec2][];
  free?: boolean;
}

/** Where the pointer lands, with the guides that put it there. */
export function smartSnap(input: SnapInput): SmartSnap {
  const { sketch, pointer, anchor, tol } = input;
  const prev = input.prevDeg ?? null;
  const done = (point: Vec2, kind: SnapKind, label: string, extra: Partial<SmartSnap> = {}): SmartSnap => ({ point, kind, label, guides: [], ...measure(anchor, prev, point), ...extra });
  if (input.free) return done(pointer, "free", "");
  // 1. corners (drawn vertices and the footprint's own corners)
  let best: { id?: string; p: Vec2; d: number } | null = null;
  for (const [id, v] of Object.entries(sketch.vertices)) {
    const d = dist(v, pointer);
    if (d <= tol && (!best || d < best.d)) best = { id, p: v, d };
  }
  for (const v of input.points ?? []) {
    const d = dist(v, pointer);
    if (d <= tol && (!best || d < best.d)) best = { p: v, d };
  }
  if (best) return done(best.p, "corner", "", best.id ? { vertex: best.id } : {});
  const lines: { a: Vec2; b: Vec2; id?: string }[] = [
    ...sketch.edges.flatMap((e) => (sketch.vertices[e.a] && sketch.vertices[e.b] ? [{ a: sketch.vertices[e.a]!, b: sketch.vertices[e.b]!, id: e.id }] : [])),
    ...(input.segments ?? []).map(([a, b]) => ({ a, b })),
  ];
  // 2. midpoints
  for (const { a, b, id } of lines) {
    const m: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    if (dist(m, pointer) <= tol * 0.8) return done(m, "midpoint", "", id ? { edge: id } : {});
  }
  // guides: extensions of lines near the pointer, and directions from the anchor
  const guides: Guide[] = [];
  const directions: { dir: Vec2; kind: Guide["kind"]; label: string }[] = [];
  for (const { a, b } of lines) {
    if (dist(a, b) < 1e-6) continue;
    const dir = unit(sub(b, a));
    const on = closestOnSegment(pointer, a, b);
    const lineQ = project(a, dir, pointer);
    if (on.t <= 0 || on.t >= 1) {
      if (dist(lineQ, pointer) <= tol * 0.7) guides.push({ kind: "extension", from: on.t <= 0 ? a : b, dir });
    }
    if (anchor) {
      directions.push({ dir, kind: "parallel", label: "" });
      directions.push({ dir: [-dir[1], dir[0]], kind: "perpendicular", label: "" });
    }
  }
  // 3. a point on a line
  for (const { a, b, id } of lines) {
    const hit = closestOnSegment(pointer, a, b);
    if (hit.distance <= tol * 0.7 && hit.t > 0.01 && hit.t < 0.99) {
      // on a line and on a direction guide from the anchor: their crossing
      if (anchor) {
        for (const d of directions) {
          const g: Guide = { kind: d.kind, from: anchor, dir: d.dir };
          const x = crossGuides(g, { kind: "extension", from: a, dir: unit(sub(b, a)) });
          if (x && dist(x, pointer) <= tol) {
            const on = closestOnSegment(x, a, b);
            if (on.t > 0.01 && on.t < 0.99) return done(x, "intersection", d.kind === "perpendicular" ? "⟂ on line" : "∥ on line", { ...(id ? { edge: id } : {}), guides: [g] });
          }
        }
      }
      return done(hit.point, "on-line", "", id ? { edge: id } : {});
    }
  }
  // 4. direction guides from the anchor: perpendicular / parallel to a drawn line, else 15° steps
  let directed: { point: Vec2; guide: Guide; label: string; off: number } | null = null;
  if (anchor && dist(anchor, pointer) > tol) {
    const want = Math.atan2(pointer[1] - anchor[1], pointer[0] - anchor[0]) / RAD;
    for (const d of directions) {
      for (const sign of [1, -1]) {
        const dir: Vec2 = [d.dir[0] * sign, d.dir[1] * sign];
        const off = Math.abs(norm180(Math.atan2(dir[1], dir[0]) / RAD - want));
        if (off <= DIRECTION_TOL_DEG && (!directed || off < directed.off)) {
          directed = { point: project(anchor, dir, pointer), guide: { kind: d.kind, from: anchor, dir }, label: d.label, off };
        }
      }
    }
    if (!directed) {
      const ref = mainDirection(sketch);
      const step = Math.round((want - ref) / ANGLE_STEP) * ANGLE_STEP + ref;
      if (Math.abs(norm180(want - step)) <= DIRECTION_TOL_DEG) {
        const dir: Vec2 = [Math.cos(step * RAD), Math.sin(step * RAD)];
        const turn = prev == null ? null : Math.round(norm180(step - prev));
        directed = { point: project(anchor, dir, pointer), guide: { kind: "angle", from: anchor, dir }, label: turn != null ? `${Math.abs(turn)}°` : `${Math.round(((step % 360) + 360) % 360)}°`, off: 0 };
      }
    }
  }
  // 5. a direction guide crossing an extension guide
  if (directed) {
    for (const g of guides) {
      const x = crossGuides(directed.guide, g);
      if (x && dist(x, directed.point) <= tol) return done(x, "intersection", "", { guides: [directed.guide, g] });
    }
    return done(directed.point, directed.guide.kind, directed.label, { guides: [directed.guide] });
  }
  if (guides.length) {
    const g = guides[0]!;
    return done(project(g.from, g.dir, pointer), "extension", "", { guides: [g] });
  }
  return done(pointer, "free", "");
}

/** The longest side a typed length may draw, in metres: a roof side, not a street (the imagery covers at most a
 * 100 m radius). QA round 1 typed 99999 and drew a side off the map. */
export const MAX_TYPED_M = 200;

function typedParts(text: string): { length: number; angle: number | null } | null {
  const m = text.trim().replace(",", ".").match(/^(\d+(?:\.\d*)?)(?:\s*[<@\s]\s*(-?\d+(?:\.\d*)?))?$/);
  if (!m) return null;
  return { length: Number(m[1]), angle: m[2] != null && m[2] !== "" ? Number(m[2]) : null };
}

/** Parses typed input: "6.5" (metres), "6.5<90" or "6.5 90" (metres and degrees); null while incomplete or out of
 * range (0, or longer than MAX_TYPED_M: `typedTooLong` says so). */
export function parseTyped(text: string): { length: number; angle: number | null } | null {
  const parts = typedParts(text);
  if (!parts || !(parts.length > 0) || parts.length > MAX_TYPED_M) return null;
  return parts;
}

/** The typed length is longer than a side may be (the readout says "At most 200 m" and Enter draws nothing). */
export function typedTooLong(text: string): boolean {
  const parts = typedParts(text);
  return !!parts && parts.length > MAX_TYPED_M;
}

/**
 * The point a typed entry puts: `length` metres from `anchor`; the direction is the typed angle as a turn from the
 * previous side (counter-clockwise positive; from east when there is none), else the pointer's (snapped) direction.
 */
export function typedPoint(anchor: Vec2, typed: { length: number; angle: number | null }, towards: Vec2, prevDeg: number | null): Vec2 {
  let deg: number;
  if (typed.angle != null) deg = (prevDeg ?? 0) + typed.angle;
  else deg = dist(anchor, towards) > 1e-9 ? Math.atan2(towards[1] - anchor[1], towards[0] - anchor[0]) / RAD : prevDeg ?? 0;
  return [anchor[0] + typed.length * Math.cos(deg * RAD), anchor[1] + typed.length * Math.sin(deg * RAD)];
}

/** "6.50 m · 90°" for the live measurement next to the cursor. */
export function measureLabel(tx: Text, s: Pick<SmartSnap, "length" | "turn" | "angle">): string {
  if (s.length == null) return "";
  const length = tx.num(s.length, s.length < 10 ? 2 : 1);
  if (s.turn != null) return tx.t("sketch.measure.lengthAngle", { length, deg: tx.num(Math.abs(Math.round(s.turn)), 0) });
  if (s.angle != null) return tx.t("sketch.measure.lengthAngle", { length, deg: tx.num(Math.round(((s.angle % 360) + 360) % 360), 0) });
  return tx.t("sketch.unit.m", { value: length });
}
